import type { ReactElement } from "react";
import { useMemo } from "react";

import styled from "styled-components";

import { mixins } from "@similarweb/styles";
import { Checkbox } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import type { EmployeeHeadcount } from "app/pages/sales-intelligence/domain/leads/types/enums";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";

import type FindEmployeeHeadcountFilter from "../../../models/EmployeeHeadcountFilter/EmployeeHeadcountFilter";

import { SELECT_ALL_ID, TRANSLATION_PREFIX } from "./const";
import { FilterContentContainer } from "./styles";
import { mappingValueToLabel } from "./utils/mappingValueToLabel";

const StyledFilterCheckbox = styled(Checkbox)`
    label {
        ${mixins.setFontVariant("body1")};
    }
`;

const EmployeeHeadcountFilter = ({
    filter,
    value,
    onFilterUpdate,
}: IFilterViewBaseProps<FindEmployeeHeadcountFilter>): ReactElement | null => {
    const isAllSelected = useMemo(() => {
        return value?.length === filter.options.length;
    }, [value, filter.options]);

    const isPartiallySelected = useMemo(() => {
        if (!value) {
            return undefined;
        }
        return value.length > 0 && value.length < filter.options.length;
    }, [value, filter.options]);

    const handleTypeAllSelection = (isSelected: boolean) => () => {
        const values: readonly EmployeeHeadcount[] | undefined = isSelected ? filter.options : undefined;
        onFilterUpdate(values);
    };

    const handleTypeSelection = (selected: EmployeeHeadcount, isSelected: boolean) => () => {
        const currentValues = value ?? [];
        const updatedValues = isSelected ? currentValues.filter((v) => v !== selected) : [...currentValues, selected];
        onFilterUpdate(updatedValues);
    };

    const renderOption = (option: EmployeeHeadcount) => {
        const isSelected = value?.includes(option) ?? false;

        return (
            <Flex
                key={option}
                alignItems="center"
            >
                <StyledFilterCheckbox
                    selected={isSelected}
                    label={mappingValueToLabel(option)}
                    onClick={handleTypeSelection(option, isSelected)}
                />
            </Flex>
        );
    };

    return (
        <FilterContentContainer>
            <Flex
                key={SELECT_ALL_ID}
                alignItems="center"
            >
                <StyledFilterCheckbox
                    selected={isAllSelected}
                    halfSelected={isPartiallySelected}
                    label={translate(`${TRANSLATION_PREFIX}.${SELECT_ALL_ID}`)}
                    onClick={handleTypeAllSelection(!isAllSelected)}
                />
            </Flex>
            {filter.options.map((item) => renderOption(item))}
        </FilterContentContainer>
    );
};

export default EmployeeHeadcountFilter;
